/**
 * Entity Configurations
 *
 * Predefined configurations for each entity type that can be rendered
 * in the UnifiedDataGrid. These configs define how the grid behaves
 * for each data source.
 */

import { EntityConfig, EntityType } from '../types/EntityConfig'

/**
 * Test entity configuration for prototype/development
 */
export const TEST_ENTITY_CONFIG: EntityConfig = {
  entityType: 'test',
  context: 'test',
  dataMode: 'client',
  treeData: false,
  getRowId: row => row.id || row.org_uuid || row.PK,

  fields: [
    { field: 'name', category: 'text', editable: false, label: 'Name' },
    { field: 'website', category: 'text', editable: true, label: 'Website' },
    { field: 'status', category: 'select', editable: true, optionsKey: 'statusOptions', label: 'Status' },
    { field: 'type', category: 'select', editable: true, optionsKey: 'typeOptions', label: 'Type' },
    { field: 'vertical', category: 'select', editable: true, optionsKey: 'verticalOptions', label: 'Vertical' },
    { field: 'owner', category: 'user_reference', editable: true, optionsKey: 'users', uuidField: 'user_uuid', label: 'Owner' },
    { field: 'primaryContact', category: 'contact', editable: true, optionsKey: 'contacts', uuidField: 'contacts_uuid', label: 'Primary Contact' },
    { field: 'team.AM', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'Account Manager' },
    { field: 'team.PM', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'Project Manager' },
    { field: 'contractedBy', category: 'org_reference', editable: true, optionsKey: 'organizations', multiple: true, uuidField: 'org_uuid', label: 'Contracted By' },
  ],

  editableFields: [
    'website',
    'status',
    'type',
    'vertical',
    'owner',
    'primaryContact',
    'team.AM',
    'team.PM',
    'contractedBy',
  ],

  cache: {
    primaryKey: ['test-grid-data'],
    dataKey: 'rows',
    searchKeyPrefixes: [],
    primaryIdField: 'PK',
    secondaryIdField: 'id',
  },

  api: {
    tableName: 'TEST_TABLE',
  },

  defaults: {
    filterModel: { items: [], quickFilterValues: [] },
    sortModel: [{ field: 'name', sort: 'asc' }],
    visibilityModel: {},
    density: 'standard',
    pinnedColumns: { left: ['__check__', 'name'] },
  },

  labels: {
    singular: 'organization',
    plural: 'organizations',
    itemLabel: 'organizations',
  },
}

/**
 * Organization entity configuration (reference for production use)
 */
export const ORGANIZATION_CONFIG: EntityConfig = {
  entityType: 'organization',
  context: 'organizations',
  dataMode: 'server',
  treeData: false,
  getRowId: row => row.org_uuid || row.PK,

  fields: [
    { field: 'name', category: 'text', editable: false, label: 'Name' },
    { field: 'website', category: 'text', editable: true, label: 'Website' },
    { field: 'status', category: 'select', editable: true, optionsKey: 'orgStatusOptions', label: 'Status' },
    { field: 'type', category: 'select', editable: true, optionsKey: 'typeOptions', label: 'Type' },
    { field: 'vertical', category: 'select', editable: true, optionsKey: 'verticalOptions', label: 'Vertical' },
    { field: 'trackingType', category: 'select', editable: true, optionsKey: 'trackingTypeOptions', label: 'Tracking Type' },
    { field: 'trackingUrl', category: 'text', editable: true, label: 'Tracking URL' },
    { field: 'owner', category: 'user_reference', editable: true, optionsKey: 'users', uuidField: 'user_uuid', label: 'Owner' },
    { field: 'primaryContact', category: 'contact', editable: true, optionsKey: 'contacts', uuidField: 'contacts_uuid', label: 'Primary Contact' },
    { field: 'billingContact', category: 'contact', editable: true, optionsKey: 'contacts', uuidField: 'contacts_uuid', label: 'Billing Contact' },
    { field: 'contacts', category: 'contact', editable: true, optionsKey: 'contacts', multiple: true, uuidField: 'contacts_uuid', label: 'Contacts' },
    { field: 'team.AM', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'Account Manager' },
    { field: 'team.PM', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'Project Manager' },
    { field: 'team.DMS', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'DMS' },
    { field: 'team.SEO', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'SEO' },
    { field: 'team.MIA', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'MIA' },
    { field: 'team.BDE', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'BDE' },
    { field: 'team.QA', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'QA' },
    { field: 'team.other', category: 'team_role', editable: true, optionsKey: 'users', multiple: true, uuidField: 'user_uuid', label: 'Other' },
    { field: 'contractedBy', category: 'org_reference', editable: true, optionsKey: 'organizations', multiple: true, uuidField: 'org_uuid', label: 'Contracted By' },
    { field: 'affiliated', category: 'org_reference', editable: true, optionsKey: 'organizations', multiple: true, uuidField: 'org_uuid', label: 'Affiliated' },
  ],

  editableFields: [
    'website',
    'status',
    'type',
    'vertical',
    'trackingType',
    'trackingUrl',
    'owner',
    'primaryContact',
    'billingContact',
    'contacts',
    'team.AM',
    'team.PM',
    'team.DMS',
    'team.SEO',
    'team.MIA',
    'team.BDE',
    'team.QA',
    'team.other',
    'contractedBy',
    'affiliated',
  ],

  cache: {
    primaryKey: ['search-data'],
    dataKey: 'organizations',
    searchKeyPrefixes: ['organizations', 'mtb'],
    primaryIdField: 'PK',
    secondaryIdField: 'org_uuid',
  },

  api: {
    tableName: 'ORGANIZATIONS_TABLE',
  },

  defaults: {
    filterModel: { items: [], quickFilterValues: [] },
    sortModel: [{ field: 'name', sort: 'asc' }],
    visibilityModel: {},
    density: 'standard',
    pinnedColumns: { left: ['__check__', 'name'] },
  },

  labels: {
    singular: 'organization',
    plural: 'organizations',
    itemLabel: 'organizations',
  },
}

/**
 * Lead entity configuration (reference for production use)
 */
export const LEAD_CONFIG: EntityConfig = {
  entityType: 'lead',
  context: 'leads',
  dataMode: 'client',
  treeData: false,
  getRowId: row => row.lead_uuid,

  fields: [
    { field: 'name', category: 'text', editable: false, label: 'Name' },
    { field: 'status', category: 'select', editable: true, optionsKey: 'leadStatusOptions', label: 'Status' },
    { field: 'quality', category: 'select', editable: true, optionsKey: 'leadQualityOptions', label: 'Quality' },
    { field: 'leadScore', category: 'number', editable: true, label: 'Lead Score' },
    { field: 'assignedUser', category: 'user_reference', editable: true, optionsKey: 'users', uuidField: 'user_uuid', label: 'Assigned To' },
  ],

  editableFields: ['status', 'quality', 'leadScore', 'assignedUser'],

  cache: {
    primaryKey: ['leads'],
    dataKey: 'leads',
    searchKeyPrefixes: [],
    primaryIdField: 'lead_uuid',
  },

  api: {
    tableName: 'LEADS_TABLE',
    singleUpdateEndpoint: '/aws/leads/update',
    deleteEndpoint: '/aws/delete/leads',
  },

  defaults: {
    filterModel: { items: [], quickFilterValues: [] },
    sortModel: [{ field: 'name', sort: 'asc' }],
    visibilityModel: {},
    density: 'standard',
  },

  labels: {
    singular: 'lead',
    plural: 'leads',
    itemLabel: 'leads',
  },
}

/**
 * Entity configuration registry
 */
export const ENTITY_CONFIGS: Record<EntityType, EntityConfig> = {
  test: TEST_ENTITY_CONFIG,
  organization: ORGANIZATION_CONFIG,
  lead: LEAD_CONFIG,
  deal: ORGANIZATION_CONFIG, // Placeholder - would need real config
  task: ORGANIZATION_CONFIG, // Placeholder - would need real config
  product: ORGANIZATION_CONFIG, // Placeholder - would need real config
}

/**
 * Get entity configuration by type
 */
export const getEntityConfig = (entityType: EntityType): EntityConfig => {
  const config = ENTITY_CONFIGS[entityType]
  if (!config) {
    throw new Error(`Unknown entity type: ${entityType}`)
  }
  return config
}

export default ENTITY_CONFIGS
